/**
 * Sample resume/JD library.
 *
 * Ten hand-curated pairs covering multiple industries and the full verdict
 * spectrum so the demo story hits every behavior the engine supports:
 *
 * IT / Tech:
 *   1. strong-swe        — direct tech stack alignment (Python/FastAPI)
 *   2. strong-data       — ML/rec systems specialist
 *   3. partial-frontend  — real overlap with real gaps (frontend → full-stack)
 *   4. transfer-medical  — cross-domain transferable skills (MD → data science)
 *   5. weak-pm-to-swe    — obvious mismatch (proves the engine says no)
 *
 * Non-IT:
 *   6. strong-nursing    — healthcare: NP → nursing director
 *   7. partial-finance   — finance: FP&A analyst → IB associate
 *   8. strong-marketing  — marketing: growth marketer → head of growth
 *   9. transfer-legal    — legal: litigation → compliance (cross-domain)
 *  10. partial-operations — operations: supply chain analyst → ops manager
 *
 * All people are fictional. Names chosen for diversity; career histories
 * written to be plausible, not cartoonish.
 */

export type SampleCategory = "direct-match" | "partial-overlap" | "cross-domain" | "mismatch";

export interface Sample {
  id: string;
  label: string;
  category: SampleCategory;
  summary: string;
  resumeText: string;
  jdText: string;
}

// ---------------------------------------------------------------------------
// 1 — Strong fit: senior backend engineer → payments platform
// ---------------------------------------------------------------------------
const STRONG_SWE: Sample = {
  id: "strong-swe",
  label: "Senior SWE → Payments Platform",
  category: "direct-match",
  summary: "Backend engineer with direct stack alignment to a fintech role.",
  resumeText: `PRIYA SHARMA
Senior Software Engineer · priya.sharma@example.com · Brooklyn, NY

SUMMARY
Backend engineer with 7+ years shipping Python microservices at scale.
Led the 2025 migration from a Django monolith to 12 FastAPI services
serving 4M MAU. Comfortable from SQL tuning to on-call incident review.

PROFESSIONAL EXPERIENCE
Staff Software Engineer · Fintrail · 2023-present
- Designed and shipped 12 FastAPI + asyncpg microservices replacing a 400K-LoC Django monolith
- Cut p99 API latency from 1.2s to 180ms by introducing Redis + pgvector cached embeddings
- Led a 5-engineer team migrating the data pipeline to Kafka + Flink; zero downtime cutover
- Owner of the company's AWS spend; negotiated Reserved Instance commitments saving $340k/yr
- Mentored 4 junior engineers through promotion to L4

Senior Software Engineer · Lumenly · 2020-2023
- Built the real-time collaboration engine in Python 3.11 + WebSockets + Redis
- Implemented SOC 2 Type II audit logging across 18 services
- Wrote the incident response runbook used during the 2022 GCP outage

Software Engineer · ClassPath · 2018-2020
- Shipped the recommendation API in Python + Postgres; 15% lift in course completion

EDUCATION
B.S. Computer Science · Columbia University · 2018

SKILLS
Python, FastAPI, PostgreSQL, Redis, Kafka, AWS (Lambda, ECS, RDS), Docker,
Kubernetes, Terraform, pgvector, LLM application patterns, distributed systems.`,
  jdText: `Senior Backend Engineer — Payments Platform
Helix Financial · Hybrid, NYC

About the role
We're rebuilding the Helix payments platform from the ground up and need a
Senior Backend Engineer who's shipped Python microservices at serious scale.
You'll own services from design through on-call, partner closely with the
data and ML teams, and help define the platform's technical direction.

Responsibilities
- Design, build, and operate high-throughput Python services using FastAPI and asyncpg
- Optimize Postgres for read/write heavy workloads (indexing, query plans, partitioning)
- Lead the migration of legacy batch jobs to a streaming architecture on Kafka
- Collaborate with ML engineers to productionize scoring models
- Participate in the on-call rotation for the payments platform
- Mentor mid-level engineers and help grow the team

Requirements
- 5+ years of backend experience, ideally in Python
- Deep expertise with FastAPI or equivalent async Python frameworks
- Strong SQL and PostgreSQL production experience
- Familiar with Kafka, Kinesis, or similar streaming systems
- Experience running services on AWS (EC2/ECS/Lambda) or equivalent cloud
- Bachelor's degree in Computer Science or equivalent experience

Nice to have
- Prior fintech or payments experience
- Experience with LLM application patterns
- Prior on-call and incident response leadership

What we offer
- Competitive salary + meaningful equity
- Excellent health, dental, vision
- 401k match
- Flexible remote/hybrid schedule`,
};

// ---------------------------------------------------------------------------
// 2 — Strong fit: ML engineer → recommendation systems
// ---------------------------------------------------------------------------
const STRONG_DATA: Sample = {
  id: "strong-data",
  label: "ML Engineer → Rec Systems",
  category: "direct-match",
  summary: "Recommendation systems specialist joining a DTC startup.",
  resumeText: `MARCUS REID
Senior Machine Learning Engineer · marcus.reid@example.com · Austin, TX

SUMMARY
ML engineer with 6 years productionizing recommendation and ranking
systems. Shipped retrieval + ranking pipelines at Spotify and Stitch Fix.
Comfortable from feature store design to online A/B testing at 100M+ user
scale. Obsessed with experiment velocity.

PROFESSIONAL EXPERIENCE
Senior ML Engineer · Stitch Fix · 2022-present
- Rebuilt the homepage recommendation pipeline — two-tower retrieval + LightGBM ranker
- Replaced a 48-hour batch refresh with a near-real-time Kafka + Redis stream; impression latency dropped from 18s to 400ms
- Owned the feature store built on Feast + Snowflake; 280 features across 40M users
- Drove a 9.1% lift in conversion through an offline-policy evaluation framework that shipped 14 experiments in Q3 alone
- Authored the team's A/B testing guide and onboarded 6 engineers onto it

ML Engineer · Spotify · 2019-2022
- Built the "daily discovery" ranker in PyTorch; shipped to 120M MAU
- Implemented multi-armed bandits for playlist cold-start; reduced week-one churn by 4.2%
- Contributed the "scikit-spotify" internal library — now used by 30+ ML teams at Spotify

Data Scientist · Ibotta · 2018-2019
- Built the first personalization model for the mobile app; 22% lift in CTR

EDUCATION
M.S. Computer Science (Machine Learning) · Georgia Tech · 2018
B.S. Mathematics · Rice University · 2016

SKILLS
Python, PyTorch, TensorFlow, LightGBM, scikit-learn, Airflow, Kafka, Spark,
Snowflake, Feast, Redis, AWS SageMaker, GCP Vertex AI, SQL, A/B testing,
multi-armed bandits, causal inference, MLflow, Weights & Biases.`,
  jdText: `Senior Machine Learning Engineer — Recommendations
Lumen Goods · Remote-first, USA

About Lumen Goods
Lumen is a DTC home essentials brand growing 3x year-over-year. We're a
small, senior team and the recommendation system drives over 40% of our
revenue. We need a Senior ML Engineer who's shipped real recommendation
pipelines in production and cares about experiment velocity as much as
model sophistication.

Responsibilities
- Own the homepage and product-detail-page recommendation ranker end-to-end
- Design and ship new models: retrieval, ranking, cold-start, diversity
- Build and maintain our feature store and offline evaluation pipeline
- Run online A/B tests and interpret results with statistical rigor
- Partner with product and design on the surfaces that use your models
- Mentor junior ML engineers as the team scales

Requirements
- 4+ years productionizing ML systems — recommendations, ranking, search, or ads
- Strong Python + one deep-learning framework (PyTorch preferred)
- Experience designing and running A/B tests at meaningful scale
- Solid SQL and comfort working with data warehouses (Snowflake, BigQuery, etc.)
- Track record of shipping fast while keeping models reliable

Nice to have
- Multi-armed bandit or contextual bandit experience
- Streaming feature pipelines (Kafka, Flink, or similar)
- Experience with Feast or other feature stores
- Retail / e-commerce recommendation background

What we offer
- Fully remote (USA) or hybrid in Austin
- Competitive cash + meaningful equity
- The rare chance to own a revenue-critical ML system at a growth-stage DTC brand`,
};

// ---------------------------------------------------------------------------
// 3 — Partial fit: senior frontend → full-stack
// ---------------------------------------------------------------------------
const PARTIAL_FRONTEND: Sample = {
  id: "partial-frontend",
  label: "Senior Frontend → Full-Stack",
  category: "partial-overlap",
  summary: "Strong frontend background, missing backend depth.",
  resumeText: `AALIYAH JOHNSON
Senior Frontend Engineer · aaliyah.johnson@example.com · Seattle, WA

SUMMARY
Frontend engineer with 5 years building performant React applications at
advertising and media companies. Obsessed with design systems, component
libraries, and shaving milliseconds off load time. Have dabbled in Node
but never owned a backend service in production.

PROFESSIONAL EXPERIENCE
Senior Frontend Engineer · Mosaic Media · 2022-present
- Lead the React 19 migration across 8 teams; rebuilt the primary editor from CRA to Vite
- Built and maintain the Mosaic Design System (42 components, used by 14 product teams)
- Reduced JS bundle size from 1.4MB to 380KB via code splitting, route-based chunks, and tree shaking
- Cut Largest Contentful Paint from 3.8s to 1.4s on the publisher landing pages
- Pair closely with designers on motion, micro-interactions, and accessibility

Frontend Engineer · Adchain Labs · 2020-2022
- Built the campaign analytics dashboard in React + TypeScript + Recharts
- Implemented the chart-interaction patterns used by 200+ advertiser accounts
- Wrote the team's accessibility checklist and migrated 40 components to WCAG 2.1 AA

Junior Software Engineer · Zyxt · 2019-2020
- Built marketing site in React + Gatsby; shipped to 12 languages

EDUCATION
B.S. Interactive Media Design · University of Washington · 2019

SKILLS
React 19, TypeScript, Next.js, Vite, Tailwind CSS, Framer Motion, Recharts,
Storybook, Jest, Playwright, CSS modules, accessibility (WCAG 2.1 AA),
design systems, performance optimization, some Node.js, some GraphQL.`,
  jdText: `Full-Stack Engineer — Platform Team
Corewave SaaS · Hybrid, San Francisco

About the role
We're building a B2B analytics platform and need a Full-Stack Engineer who
can take a feature from database schema through the React UI, and then
carry the pager when it breaks at 2am. You'll be one of eight engineers
on the Platform team, reporting to the CTO.

Responsibilities
- Own features end-to-end: data model, API, frontend, tests, and release
- Build new React + TypeScript UI in our Next.js app
- Design and ship REST and tRPC endpoints backed by Postgres
- Participate in the on-call rotation (weekly, with 3 other engineers)
- Help grow the Platform team as we scale from 120 to 400 customers

Requirements
- 4+ years of full-stack experience in production
- Strong React + TypeScript; comfortable in Next.js
- Strong Node.js backend experience
- Solid Postgres production experience — schema design, migrations, query tuning
- Comfort with Docker, CI/CD, and basic AWS primitives
- Willing to do on-call

Nice to have
- tRPC or equivalent typed API experience
- Experience with Playwright or another E2E testing tool
- Design system experience

What we offer
- Competitive cash + equity
- Meaningful ownership — you'll ship code in your first week
- Hybrid in SF (3 days in office) or fully remote after 90 days`,
};

// ---------------------------------------------------------------------------
// 4 — Cross-domain transfer: MD/PhD researcher → clinical data scientist
// ---------------------------------------------------------------------------
const TRANSFER_MEDICAL: Sample = {
  id: "transfer-medical",
  label: "MD/PhD → Clinical Data Scientist",
  category: "cross-domain",
  summary: "Cross-domain transferable skills: clinical research → health data.",
  resumeText: `DANIEL CHEN, MD, PhD
Clinical Research Physician · daniel.chen@example.com · Boston, MA

SUMMARY
Physician-scientist with 8 years leading clinical outcomes research at
academic medical centers. Built statistical models for survival analysis
and treatment effect estimation across 12 observational cohort studies.
First- or senior-author on 14 peer-reviewed publications including work in
NEJM and The Lancet. Ready to transition to industry health data science.

PROFESSIONAL EXPERIENCE
Instructor in Medicine · Massachusetts General Hospital / Harvard Medical School · 2021-present
- Principal investigator on a 4,200-patient observational study of GLP-1 agonist
  cardiovascular outcomes — published in NEJM, 2024
- Designed the statistical analysis plan: propensity-score matching, Cox regression,
  multiple imputation, sensitivity analyses
- Built and maintain reproducible R pipelines for 3 ongoing studies using targets + renv
- Led the IRB submission and FDA pre-submission correspondence for a prospective study
- Mentored 5 junior researchers on applied statistics and reproducible research practices

Clinical Research Fellow · Brigham and Women's Hospital · 2019-2021
- Conducted secondary analyses on the Framingham Heart Study long-running cohort
- Published 6 peer-reviewed papers including 2 in The Lancet
- Co-developed the survival-analysis teaching materials used by the Harvard T.H. Chan
  School of Public Health biostatistics course

Resident Physician (Internal Medicine) · Brigham and Women's Hospital · 2016-2019

EDUCATION
MD · Johns Hopkins School of Medicine · 2016
PhD, Epidemiology · Johns Hopkins Bloomberg School of Public Health · 2016
B.S. Biology · Stanford University · 2010

SKILLS
R (tidyverse, survival, tidymodels, targets, renv), SQL, Python (pandas, scikit-learn,
intro level), statistical modeling, survival analysis, causal inference, propensity
scoring, observational study design, IRB process, FDA regulatory submissions, medical
literature review, scientific writing, clinical knowledge across internal medicine.`,
  jdText: `Senior Clinical Data Scientist
Mercia Health AI · Remote, USA

About Mercia
Mercia Health AI builds machine learning tools that help health systems
identify high-risk patients before crises happen. Our models are used in
38 hospitals across the US. We're hiring a Senior Clinical Data Scientist
to bridge our ML engineering team and clinical customers — someone who
reads a research paper and writes the Python that validates it.

Responsibilities
- Design and validate ML models for clinical outcomes (risk stratification,
  readmission prediction, adverse event detection)
- Own the statistical rigor of our model evaluation — calibration, fairness
  audits, subgroup analyses, confidence intervals
- Partner with the ML engineering team to productionize models and with
  clinical customers to understand deployment constraints
- Contribute to FDA submissions for our De Novo 510(k) pathway
- Publish our validation work in clinical journals

Requirements
- Advanced degree in a quantitative + clinical field (PhD in epidemiology,
  biostatistics, health services research, or equivalent; MD/PhD welcomed)
- Deep experience with observational study design and causal inference
- Strong statistical modeling skills — survival analysis, regression, propensity
  scoring, sensitivity analyses
- Working proficiency in Python (we don't need ML expertise at hire; we need
  someone who can learn fast and writes honest code)
- Strong writing ability; we publish

Nice to have
- Prior experience with SQL at scale
- Exposure to scikit-learn or PyTorch
- Prior FDA regulatory experience
- Peer-reviewed publications in clinical outcomes research

What we offer
- Fully remote
- Competitive cash + meaningful equity
- A mission-driven team where your clinical training is a daily asset, not
  a costume to put on for investor decks`,
};

// ---------------------------------------------------------------------------
// 5 — Weak fit: senior PM → senior backend engineer
// ---------------------------------------------------------------------------
const WEAK_PM_TO_SWE: Sample = {
  id: "weak-pm-to-swe",
  label: "Senior PM → Senior Backend Engineer",
  category: "mismatch",
  summary: "Career-change attempt with real mismatch. Demonstrates the engine says no.",
  resumeText: `DEREK SINGH
Senior Product Manager · derek.singh@example.com · Chicago, IL

SUMMARY
Product leader with 7 years shipping B2B SaaS products. Led the 0-to-1
product strategy for two successful product lines and built the PM
function from scratch at a Series B startup. Strong operator with an
MBA and a growing interest in the backend engineering side of the stack.

PROFESSIONAL EXPERIENCE
Senior Product Manager · Velora · 2022-present
- Owned the product strategy for the Velora Workflow product line, growing ARR from $2M to $14M in 18 months
- Led cross-functional teams of 14 (engineering, design, data, support) through quarterly delivery cycles
- Established the product's A/B testing culture — 42 experiments shipped in 2024
- Ran monthly customer interview cadence with 30+ customers; turned findings into the 2025 roadmap
- Represented product in 14 sales calls that closed $4.2M in enterprise deals

Product Manager · Klynn · 2020-2022
- Shipped 3 features that accounted for 28% of net-new ARR in FY2022
- Wrote the PRD template now used across the whole Klynn product org
- Partnered with engineering leads on technical trade-off conversations

Associate Product Manager · Big Four Consulting Firm (client rotation) · 2018-2020
- Rotated through 6 client engagements focused on digital product strategy
- Co-authored 2 published case studies on B2B onboarding optimization

EDUCATION
MBA · Kellogg School of Management · Northwestern University · 2018
B.A. Economics · University of Michigan · 2014

SKILLS
Product strategy, roadmapping, stakeholder management, customer interviews,
A/B testing frameworks, SQL (intermediate), Mixpanel, Amplitude, Looker, JIRA,
Figma, PRDs, OKRs, pricing, positioning, basic Python scripting.`,
  jdText: `Senior Backend Engineer
Flux Infrastructure · Hybrid, San Francisco

About the role
Flux Infrastructure builds the data plane that powers real-time
communication for 400+ enterprise customers. We're hiring a Senior
Backend Engineer to own critical services in our Go + Python platform.
This is a hands-on IC role. No product management, no people management,
no roadmapping meetings.

Responsibilities
- Design, build, and operate Go microservices handling 2M events/second
- Own distributed systems concerns: consistency, replication, fault tolerance
- Deep-dive Postgres performance — partitioning, query plans, index tuning
- Participate in 24/7 on-call for production systems
- Write deep technical design docs that other senior engineers tear apart
- Review code from other senior engineers and push back hard on shortcuts

Requirements
- 6+ years of backend engineering experience in a compiled language (Go, Java, Rust, C++)
- Production experience with distributed systems (consensus, replication, CAP trade-offs)
- Strong Postgres or equivalent relational database internals knowledge
- Track record of owning services through design, build, launch, and 2am incidents
- Comfort in 6-hour debugging sessions in tcpdump, gdb, or equivalent
- Ability to write a 20-page technical RFC and defend it against 8 senior engineers

What we offer
- Very competitive cash + significant equity
- A team where code review is the main artifact of communication
- A promise that you will never attend a standup longer than 10 minutes`,
};

// ---------------------------------------------------------------------------
// 6 — Strong fit: nurse practitioner → nursing director (healthcare)
// ---------------------------------------------------------------------------
const STRONG_NURSING: Sample = {
  id: "strong-nursing",
  label: "Nurse Practitioner → Nursing Director",
  category: "direct-match",
  summary: "Clinical leadership with strong overlap in healthcare.",
  resumeText: `MARIA GUTIERREZ, DNP, APRN, FNP-C
Nurse Practitioner · maria.gutierrez@example.com · Houston, TX

SUMMARY
Board-certified Family Nurse Practitioner with 10 years of progressive
clinical leadership across acute care, primary care, and population health.
Currently leading a 22-nurse team at a Level I trauma center while
maintaining a patient panel of 1,200. Passionate about care quality metrics,
nurse retention, and evidence-based practice.

PROFESSIONAL EXPERIENCE
Lead Nurse Practitioner · Houston Methodist Hospital · 2021-present
- Manage a team of 22 RNs and 4 NPs across the Internal Medicine department
- Reduced 30-day readmission rates by 18% through a nurse-led transitional care program
- Designed the department's sepsis early-warning protocol adopted by 3 other units
- Achieved 94% patient satisfaction score (Press Ganey) for 6 consecutive quarters
- Led the successful Joint Commission accreditation survey for the department
- Mentor 8 new-graduate NPs per year through the hospital's residency program

Nurse Practitioner · Texas Health Physicians Group · 2018-2021
- Managed a primary care panel of 1,400 patients across 3 clinics
- Implemented chronic disease management protocols reducing HbA1c by avg 1.2% for diabetic patients
- Trained 12 medical assistants on point-of-care testing procedures

Registered Nurse · Memorial Hermann Hospital · 2014-2018
- ICU nurse with CCRN certification; managed ventilated patients, vasoactive drips
- Charge nurse responsibilities including staffing, bed management, rapid response

EDUCATION
Doctor of Nursing Practice (DNP) · University of Texas Health Science Center · 2021
Master of Science in Nursing (FNP) · Baylor University · 2018
Bachelor of Science in Nursing · Texas A&M University · 2014

CERTIFICATIONS
FNP-BC (AANP), CCRN, BLS, ACLS, PALS

SKILLS
Clinical leadership, care quality improvement, patient satisfaction metrics,
EHR optimization (Epic, Cerner), population health management, chronic disease
management, nurse mentorship, Joint Commission compliance, HIPAA, evidence-based
practice, interdisciplinary collaboration, staffing and scheduling.`,
  jdText: `Director of Nursing — Inpatient Services
Lone Star Health System · Houston, TX

About the Role
Lone Star Health System is a 450-bed community hospital serving the greater
Houston area. We're seeking a Director of Nursing for our Inpatient Services
division to lead clinical operations across Medical/Surgical, Telemetry, and
Progressive Care units (120 beds, 180 staff). You'll report to the CNO and
own quality, safety, staffing, and budget for the division.

Responsibilities
- Lead clinical operations for 3 inpatient units with 180 nursing staff
- Own quality metrics: readmission rates, falls, CLABSI, CAUTI, patient satisfaction
- Manage the annual $12M nursing budget including staffing ratios and agency usage
- Drive nurse retention initiatives — current turnover is 24%, target is 15%
- Ensure regulatory compliance with Joint Commission, CMS, and state standards
- Partner with physicians, case management, and pharmacy on care coordination
- Implement evidence-based practice changes and clinical protocols
- Mentor and develop nurse managers and charge nurses

Requirements
- DNP or MSN required; DNP preferred
- Active RN license in Texas
- 7+ years of progressive nursing leadership experience
- Demonstrated success improving quality metrics (readmissions, patient safety)
- Experience managing teams of 50+ nursing staff
- Familiarity with Epic EHR
- Joint Commission survey preparation experience

Nice to Have
- ANCC Nurse Executive certification (NEA-BC)
- Experience in a Level I or Level II trauma center
- Prior budget management experience
- Magnet designation experience

Compensation
- Competitive salary commensurate with experience
- Full benefits including medical, dental, vision, 403(b) match
- Tuition reimbursement and continuing education support`,
};

// ---------------------------------------------------------------------------
// 7 — Partial fit: financial analyst → investment banking associate (finance)
// ---------------------------------------------------------------------------
const PARTIAL_FINANCE: Sample = {
  id: "partial-finance",
  label: "Financial Analyst → IB Associate",
  category: "partial-overlap",
  summary: "Financial modeling skills overlap, but missing deal execution and LBO expertise.",
  resumeText: `JAMES OKAFOR
Senior Financial Analyst · james.okafor@example.com · New York, NY

SUMMARY
Financial analyst with 4 years at a Fortune 500 consumer goods company.
Built the FP&A forecasting models used by the CFO's office for quarterly
earnings guidance. Strong in Excel modeling, SQL, and financial reporting.
Looking to transition to investment banking.

PROFESSIONAL EXPERIENCE
Senior Financial Analyst · Procter & Gamble · 2022-present
- Built the 3-statement financial model used for quarterly earnings guidance to Wall Street
- Developed scenario analysis frameworks for M&A targets (3 completed evaluations)
- Automated monthly reporting package reducing close cycle from 8 to 4 business days
- Created variance analysis dashboards in Tableau tracking $2.4B revenue across 6 business units
- Presented financial results to VP-level stakeholders monthly

Financial Analyst · Procter & Gamble · 2020-2022
- Maintained the rolling 12-month revenue forecast for the North America Beauty division ($800M)
- Built the DCF model used to evaluate 2 acquisition targets
- Performed due diligence financial analysis on a $340M bolt-on acquisition
- Reconciled intercompany transactions across 14 entities

EDUCATION
B.S. Finance · NYU Stern School of Business · 2020

CERTIFICATIONS
CFA Level II Candidate (June 2026 exam)

SKILLS
Financial modeling (3-statement, DCF, LBO basics), Excel (advanced — VBA, Power Query),
SQL, Tableau, SAP, Bloomberg Terminal, GAAP, variance analysis, budgeting, forecasting,
M&A evaluation basics, PowerPoint, financial reporting.`,
  jdText: `Investment Banking Associate — Healthcare Group
Meridian Partners · New York, NY

About Meridian
Meridian Partners is a middle-market investment bank focused on healthcare
services and health IT. We advise on sell-side M&A, capital raises, and
strategic advisory for companies in the $50M-$500M enterprise value range.

Responsibilities
- Execute M&A and capital raising transactions from pitch through close
- Build detailed financial models: DCF, LBO, accretion/dilution, trading comps
- Draft confidential information memorandums (CIMs) and management presentations
- Conduct industry research and competitive analysis for healthcare sub-sectors
- Manage due diligence processes and coordinate with legal, accounting, and tax advisors
- Maintain and develop client relationships alongside senior bankers
- Mentor analysts and review their work product

Requirements
- 3-5 years of investment banking, private equity, or equivalent transaction experience
- Expert-level financial modeling — LBO, DCF, merger models, trading/transaction comps
- Demonstrated deal execution experience (at least 2 closed transactions)
- Healthcare sector knowledge preferred
- Strong written and verbal communication
- MBA or equivalent; CFA a plus
- Ability to work demanding hours during live deal processes

Nice to Have
- Prior healthcare investment banking experience
- Private equity or growth equity background
- Existing relationships in the healthcare services space

Compensation
- Base salary: $175K-$225K depending on experience
- Annual bonus: 50-100% of base
- Full benefits, 401(k) match`,
};

// ---------------------------------------------------------------------------
// 8 — Strong fit: content marketing → head of growth (marketing)
// ---------------------------------------------------------------------------
const STRONG_MARKETING: Sample = {
  id: "strong-marketing",
  label: "Content Marketing → Head of Growth",
  category: "direct-match",
  summary: "Growth marketer with proven funnel metrics applying to lead a team.",
  resumeText: `SARAH KWAN
Senior Content & Growth Marketing Manager · sarah.kwan@example.com · San Francisco, CA

SUMMARY
Growth marketer with 6 years driving pipeline and revenue through content,
SEO, and demand generation at B2B SaaS companies. Built the content engine
at a Series B startup from zero to 14K MQLs/month and $8M influenced pipeline.
Equal parts strategist and operator — I write the blog posts, build the
HubSpot workflows, and analyze the attribution data.

PROFESSIONAL EXPERIENCE
Senior Growth Marketing Manager · Cloudvault (Series C, $120M ARR) · 2023-present
- Own top-of-funnel demand generation: content, SEO, paid social, webinars, email
- Grew organic traffic from 45K to 180K monthly sessions through a 12-month SEO overhaul
- Built the content-to-pipeline attribution model in HubSpot + Looker; proved content
  drives 34% of enterprise pipeline
- Launched the company's first podcast (22 episodes, 8K monthly downloads)
- Manage a $480K annual content budget and 4 freelance writers

Content Marketing Manager · Relay (Series B) · 2020-2023
- Built the content marketing function from scratch — strategy, hiring, execution
- Published 180+ blog posts, 12 whitepapers, and 6 customer case studies
- Grew monthly organic MQLs from 200 to 14,000 in 24 months
- Implemented the lead scoring model in HubSpot that increased sales-accepted lead
  rate from 12% to 31%
- Managed relationships with 8 industry analysts and 20+ podcast hosts

Marketing Coordinator · TechBridge Media · 2018-2020
- Wrote email campaigns, social copy, and landing pages for 6 B2B SaaS clients
- Managed client social accounts totaling 120K followers

EDUCATION
B.A. English & Communications · UC Berkeley · 2018

SKILLS
Content strategy, SEO (Ahrefs, Semrush), demand generation, HubSpot (Marketing Hub,
CMS, workflows, lead scoring), Google Analytics, Looker, paid social (LinkedIn, Meta),
email marketing, landing page optimization, conversion rate optimization, webinars,
podcasting, marketing attribution, B2B SaaS, copywriting, editorial management.`,
  jdText: `Head of Growth
Nimbly · Remote-first, USA

About Nimbly
Nimbly is a Series B project management platform for mid-market companies.
We have 2,200 customers, $18M ARR, and are growing 80% year-over-year.
We're hiring a Head of Growth to own the full acquisition funnel — from
first touch to closed-won — and build the growth team from 3 to 10.

Responsibilities
- Own the growth strategy: content, SEO, paid acquisition, email, partnerships
- Build and manage the growth marketing team (currently 3, target 10 by year-end)
- Drive pipeline targets: $24M influenced pipeline in 2026
- Own the marketing tech stack (HubSpot, Looker, analytics tooling)
- Build multi-touch attribution models and report pipeline contribution to the exec team
- Partner with Sales on lead scoring, SLAs, and funnel conversion optimization
- Manage a $1.2M annual growth budget across channels

Requirements
- 5+ years in B2B SaaS growth or demand generation marketing
- Proven track record growing organic pipeline — show us the numbers
- Deep HubSpot expertise (Marketing Hub, CMS, workflows, lead scoring)
- Experience building and managing a marketing team
- Strong analytical skills — attribution modeling, cohort analysis, CAC/LTV
- Excellent writer who can produce executive-quality content and coach others
- Experience managing a $500K+ marketing budget

Nice to Have
- SEO expertise (technical + content)
- Podcast or webinar experience
- Prior Series B-C experience scaling from $10M to $50M ARR
- Product-led growth exposure

Compensation
- Competitive base + equity
- Full benefits
- Fully remote with quarterly team off-sites`,
};

// ---------------------------------------------------------------------------
// 9 — Cross-domain transfer: litigation attorney → compliance officer (legal)
// ---------------------------------------------------------------------------
const TRANSFER_LEGAL: Sample = {
  id: "transfer-legal",
  label: "Litigation Attorney → Compliance Officer",
  category: "cross-domain",
  summary: "Cross-domain within legal: litigation skills partially transfer but compliance program experience is missing.",
  resumeText: `RACHEL TORRES, JD
Senior Litigation Associate · rachel.torres@example.com · Washington, DC

SUMMARY
Litigation attorney with 6 years of trial and appellate practice at a
top-20 Am Law firm. Managed a docket of 30+ cases including complex
commercial litigation, regulatory enforcement actions, and internal
investigations. Strong legal research and writing skills — have drafted
50+ briefs and 3 winning appellate arguments.

PROFESSIONAL EXPERIENCE
Senior Associate · Morrison & Sterling LLP · 2021-present
- Lead attorney on 12 active commercial litigation matters ($5M-$80M in dispute)
- Managed 3 internal investigations involving potential FCPA and securities violations
- Drafted and argued 2 successful motions for summary judgment in federal court
- First-chaired 4 depositions and second-chaired a 3-week jury trial
- Supervised 6 junior associates and coordinated document review teams of 15+
- Advised clients on litigation risk assessment and regulatory exposure

Associate · Morrison & Sterling LLP · 2018-2021
- Drafted briefs, motions, and discovery requests across 25+ matters
- Conducted legal research on SEC enforcement trends and compliance frameworks
- Prepared witnesses for deposition and trial testimony
- Managed e-discovery workflows using Relativity for cases with 2M+ documents

EDUCATION
JD · Georgetown University Law Center · 2018 (cum laude, Law Review)
B.A. Political Science · University of Virginia · 2015

BAR ADMISSIONS
District of Columbia, New York, Virginia

SKILLS
Complex commercial litigation, regulatory enforcement, internal investigations,
FCPA, securities law, legal research (Westlaw, Lexis), brief writing, deposition
preparation, e-discovery (Relativity), trial preparation, witness preparation,
client counseling, legal risk assessment, team supervision.`,
  jdText: `Chief Compliance Officer
Ascend Financial Technologies · New York, NY

About Ascend
Ascend is a fintech company providing institutional-grade trading
infrastructure to hedge funds and asset managers. We process $8B in daily
trading volume and are regulated by the SEC and FINRA. We're hiring a
Chief Compliance Officer to build and lead our compliance function as we
prepare for our next phase of growth.

Responsibilities
- Build and lead the compliance program from the ground up (first dedicated hire)
- Design and implement compliance policies for SEC, FINRA, and state regulations
- Conduct ongoing risk assessments and manage the compliance monitoring program
- Oversee regulatory filings, registrations, and examination responses
- Lead internal investigations and manage escalation procedures
- Train all employees on compliance policies, insider trading, and conflicts of interest
- Report directly to the CEO and Board on compliance matters
- Manage relationships with external counsel and regulators

Requirements
- 8+ years in compliance, regulatory, or legal roles in financial services
- Deep knowledge of SEC and FINRA regulations governing broker-dealers or investment advisers
- Experience building compliance programs or significantly expanding existing ones
- Strong writing skills for policy drafting and regulatory correspondence
- JD required; compliance certifications (CCEP, CRCM) a plus
- Ability to translate regulatory requirements into practical business processes
- Experience managing regulatory examinations and audits

Nice to Have
- Prior experience at a fintech or trading technology company
- FINRA Series 7, 24, or 14 licenses
- Experience with AML/KYC program design
- Prior chief compliance officer or deputy CCO experience

Compensation
- Competitive base salary + equity
- Full benefits
- High-visibility role reporting to CEO and Board`,
};

// ---------------------------------------------------------------------------
// 10 — Partial fit: supply chain analyst → operations manager (operations)
// ---------------------------------------------------------------------------
const PARTIAL_OPERATIONS: Sample = {
  id: "partial-operations",
  label: "Supply Chain Analyst → Operations Manager",
  category: "partial-overlap",
  summary: "Strong analytical foundation but missing people management experience.",
  resumeText: `KEVIN PARK
Senior Supply Chain Analyst · kevin.park@example.com · Dallas, TX

SUMMARY
Supply chain analyst with 5 years optimizing procurement, inventory, and
logistics for a Fortune 200 retailer. Built the demand forecasting model
that reduced stockouts by 32% across 400 stores. Strong in data analysis
and process improvement, looking to move into operations management.

PROFESSIONAL EXPERIENCE
Senior Supply Chain Analyst · Target Corporation · 2022-present
- Built the demand forecasting model (Python + XGBoost) that reduced stockouts by 32%
  across 400 stores in the seasonal goods category
- Optimized the DC-to-store replenishment algorithm saving $4.2M annually in freight costs
- Led a 6-month Lean Six Sigma project reducing order processing time from 48hrs to 18hrs
- Created executive dashboards in Tableau tracking $2.8B in annual procurement spend
- Collaborate with 8 category merchants on seasonal buy planning

Supply Chain Analyst · Target Corporation · 2020-2022
- Maintained safety stock models for 12,000 SKUs across 3 distribution centers
- Automated weekly vendor scorecard reporting (Excel VBA → Python pipeline)
- Supported the vendor negotiations team with cost analysis and total cost of ownership models

Logistics Coordinator · Amazon · 2019-2020
- Managed last-mile delivery scheduling for a 200-driver Dallas hub
- Reduced late deliveries by 15% through route optimization analysis

EDUCATION
B.S. Industrial Engineering · Texas A&M University · 2019

CERTIFICATIONS
Lean Six Sigma Green Belt (ASQ)

SKILLS
Demand forecasting, inventory optimization, Lean Six Sigma, Python (pandas,
scikit-learn, XGBoost), SQL, Tableau, Excel (advanced), SAP, procurement
analysis, logistics optimization, vendor management, process improvement,
data visualization, cost modeling.`,
  jdText: `Operations Manager — Distribution & Fulfillment
NovaMart · Dallas, TX

About NovaMart
NovaMart is a fast-growing e-commerce retailer with 3 fulfillment centers
processing 80,000 orders per day. We're hiring an Operations Manager to
lead our largest facility (Dallas, 350 associates, 35,000 daily orders).

Responsibilities
- Manage all daily operations at the Dallas fulfillment center (350 hourly associates)
- Own KPIs: order accuracy (target 99.8%), throughput, cost per unit, safety incidents
- Lead a team of 6 shift supervisors and 2 assistant managers
- Drive continuous improvement using Lean/Six Sigma methodologies
- Manage the annual operating budget ($18M) and capital expenditure requests
- Coordinate with supply chain, transportation, and inventory planning teams
- Ensure OSHA compliance and maintain a culture of workplace safety
- Handle employee relations, performance management, and hiring for 350-person facility

Requirements
- 5+ years in operations, supply chain, or logistics management
- 3+ years of direct people management experience (50+ reports)
- Demonstrated P&L or budget management responsibility
- Experience with Lean Six Sigma or equivalent continuous improvement methodologies
- Strong analytical skills — comfortable with data-driven decision making
- Bachelor's degree in engineering, supply chain, business, or equivalent
- Ability to work flexible hours including occasional weekends during peak season

Nice to Have
- Experience in e-commerce fulfillment or distribution center operations
- Six Sigma Black Belt certification
- WMS (Warehouse Management System) experience
- Bilingual English/Spanish

Compensation
- Competitive salary + annual performance bonus
- Full benefits including medical, dental, 401(k) match
- Relocation assistance if needed`,
};

// ---------------------------------------------------------------------------
// Public registry
// ---------------------------------------------------------------------------
export const SAMPLES: readonly Sample[] = [
  STRONG_SWE,
  STRONG_DATA,
  PARTIAL_FRONTEND,
  TRANSFER_MEDICAL,
  WEAK_PM_TO_SWE,
  STRONG_NURSING,
  PARTIAL_FINANCE,
  STRONG_MARKETING,
  TRANSFER_LEGAL,
  PARTIAL_OPERATIONS,
] as const;

export function getSampleById(id: string): Sample | undefined {
  return SAMPLES.find((s) => s.id === id);
}
